import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';
import Header from '../../components/Header';
import QueueItem from '../../components/Upload/QueueItem';
import ReprocessDocumentModal from '../../components/Documents/ReprocessDocumentModal';
import ReprocessInPlaceModal from '../../components/Documents/ReprocessInPlaceModal';
import { useUpload } from '../../hooks/useUpload';
import { useArcaFeature } from '../../hooks/useArcaFeature';
import { useApi } from '../../hooks/useApi';
import { getTenant } from '../../common/tenantApi';
import { constatarDocumentArca } from '../../common/apiClient';
import { loadReprocessContext } from '../../utils/reprocess';
import { reprocessDocumentAs, reprocessToastMessage } from '../../utils/uploadReprocess';
import {
  inPlaceReprocessToastMessage,
  isInPlaceReprocessableFromQueueItem,
  runInPlaceReprocess,
} from '../../utils/documentReprocess';
import './Upload.css';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.zip';

export default function UploadPage() {
  const navigate = useNavigate();
  const { callApi } = useApi();
  const { items, addFiles, retry, remove, patchItem } = useUpload();
  const { enabled: arcaEnabled, canConstatar } = useArcaFeature();
  const [arcaLoadingId, setArcaLoadingId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [emailAlias, setEmailAlias] = useState('');
  const [copyToast, setCopyToast] = useState(false);
  const [actionToast, setActionToast] = useState(null);
  const [reprocessDocTypes, setReprocessDocTypes] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [reprocessModal, setReprocessModal] = useState(null);
  const [inPlaceModal, setInPlaceModal] = useState(null);
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getTenant()
      .then((tenant) => {
        if (!cancelled) setEmailAlias(tenant?.ingest_email || '');
      })
      .catch(() => {
        if (!cancelled) setEmailAlias('');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getIdTokenClaims } = await import('../../common/auth');
        const ctx = await loadReprocessContext(callApi, getIdTokenClaims);
        if (!cancelled) {
          setReprocessDocTypes(ctx.reprocessDocTypes || []);
          setCanEdit(ctx.canEdit);
        }
      } catch {
        if (!cancelled) setReprocessDocTypes([]);
      }
    })();
    return () => { cancelled = true; };
  }, [callApi]);

  const onOpenDocument = useCallback((docId) => {
    if (!docId) return;
    navigate(`/documents/${docId}`);
  }, [navigate]);

  const handleArcaConstatar = useCallback(async (item) => {
    if (!item?.doc_id || !canConstatar) return;
    setArcaLoadingId(item.id);
    try {
      const result = await constatarDocumentArca(item.doc_id);
      patchItem(item.id, { arca_validation: result?.arca_validation || null });
      setActionToast({ severity: 'success', message: 'Validación ARCA completada' });
    } catch (err) {
      setActionToast({
        severity: 'error',
        message: err?.message || 'No se pudo validar con ARCA',
      });
    } finally {
      setArcaLoadingId(null);
    }
  }, [canConstatar, patchItem]);

  const onReclassify = useCallback((item) => {
    if (!item?.doc_id) return;
    setReprocessModal({
      document_id: item.doc_id,
      filename: item.name,
      queueId: item.id,
    });
  }, []);

  const closeReprocessModal = () => {
    if (!reprocessLoading) setReprocessModal(null);
  };

  const closeInPlaceModal = () => {
    if (!reprocessLoading) setInPlaceModal(null);
  };

  const onReprocessInPlace = useCallback((item) => {
    if (!item?.doc_id) return;
    setInPlaceModal({
      document_id: item.doc_id,
      filename: item.name,
      queueId: item.id,
    });
  }, []);

  const executeInPlaceReprocess = async () => {
    if (!inPlaceModal?.document_id) return;
    const docId = inPlaceModal.document_id;
    const queueId = inPlaceModal.queueId;
    setReprocessLoading(true);
    try {
      const result = await runInPlaceReprocess(callApi, docId);
      const severity = result?.status === 'QUEUED' ? 'success' : 'error';
      setActionToast({
        severity,
        message: inPlaceReprocessToastMessage(result),
      });
      if (result?.status === 'QUEUED') {
        patchItem?.(queueId, {
          status: 'reprocessing',
          doc_id: docId,
          error: null,
        });
        setInPlaceModal(null);
      }
    } catch (err) {
      setActionToast({
        severity: 'error',
        message: err?.message || 'Error al reprocesar documento',
      });
    } finally {
      setReprocessLoading(false);
    }
  };

  const executeReclassify = async (targetDocType) => {
    if (!reprocessModal?.document_id || !targetDocType) return;
    setReprocessLoading(true);
    try {
      const result = await reprocessDocumentAs(
        callApi,
        reprocessModal.document_id,
        targetDocType,
      );
      const severity = result?.status === 'QUEUED' ? 'success' : result?.status === 'ALREADY_EXISTS' ? 'info' : 'error';
      setActionToast({
        severity,
        message: reprocessToastMessage(result, targetDocType),
      });
      if (result?.status === 'QUEUED' || result?.status === 'ALREADY_EXISTS') {
        patchItem?.(reprocessModal.queueId, {
          status: 'processing',
          doc_type: result?.redirect_target_doc_type || targetDocType,
          doc_id: result?.redirect_target_document_id || reprocessModal.document_id,
          error: null,
        });
      }
      setReprocessModal(null);
    } catch (err) {
      setActionToast({
        severity: 'error',
        message: err?.message || 'Error al reclasificar documento',
      });
    } finally {
      setReprocessLoading(false);
    }
  };

  const handleFiles = useCallback((fileList) => {
    if (fileList?.length) addFiles(fileList);
  }, [addFiles]);

  const onDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    handleFiles(event.dataTransfer.files);
  };

  const copyEmail = async () => {
    if (!emailAlias) return;
    try {
      await navigator.clipboard.writeText(emailAlias);
      setCopyToast(true);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="upload-files-page">
      <Header />
      <div className="upload-page">
        <h1 className="upload-page-title">Subir Archivo</h1>
        <p className="upload-page-sub">
          Arrastrá comprobantes o seleccioná archivos desde tu equipo. También podés enviarlos por email al workspace.
        </p>

        <div
          className={`upload-dropzone ${dragOver ? 'dragover' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M17 8l-5-5-5 5M12 3v12" />
          </svg>
          <h2>Arrastrá archivos acá</h2>
          <p>PDF, imágenes (JPG, PNG) o ZIP · máx. 25 MB por archivo</p>
          <button
            type="button"
            className="upload-btn-primary"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Seleccionar archivos
          </button>
          <p className="hint">Los archivos se procesan automáticamente al completar la carga</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT}
            hidden
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {items.length > 0 && (
          <div className="upload-queue">
            <h3>Cola de carga</h3>
            {items.map((item) => (
              <QueueItem
                key={item.id}
                item={item}
                onRetry={retry}
                onRemove={remove}
                onOpen={onOpenDocument}
                onReclassify={reprocessDocTypes.length ? onReclassify : undefined}
                onReprocess={canEdit ? onReprocessInPlace : undefined}
                canReprocess={canEdit && isInPlaceReprocessableFromQueueItem(item, { canEdit })}
                arcaEnabled={arcaEnabled}
                canConstatar={canConstatar}
                onConstatar={handleArcaConstatar}
                arcaLoading={arcaLoadingId === item.id}
              />
            ))}
          </div>
        )}

        <div className="upload-email-box">
          <h3>Ingest por email</h3>
          <p>Enviá comprobantes a la casilla del workspace y recibí confirmación automática:</p>
          <div className="upload-email-row">
            <code>{emailAlias || 'Cargando…'}</code>
            {emailAlias && (
              <button type="button" className="upload-email-copy" onClick={copyEmail}>
                Copiar
              </button>
            )}
          </div>
        </div>
      </div>

      <ReprocessDocumentModal
        isOpen={Boolean(reprocessModal)}
        filename={reprocessModal?.filename}
        docTypes={reprocessDocTypes}
        loading={reprocessLoading}
        onConfirm={executeReclassify}
        onCancel={closeReprocessModal}
      />

      <ReprocessInPlaceModal
        isOpen={Boolean(inPlaceModal)}
        filename={inPlaceModal?.filename}
        loading={reprocessLoading}
        onConfirm={executeInPlaceReprocess}
        onCancel={closeInPlaceModal}
      />

      <Snackbar
        open={copyToast}
        autoHideDuration={2500}
        onClose={() => setCopyToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setCopyToast(false)}>
          Email copiado
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(actionToast)}
        autoHideDuration={6000}
        onClose={() => setActionToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 72, sm: 80 } }}
      >
        <Alert
          severity={actionToast?.severity || 'info'}
          variant="filled"
          onClose={() => setActionToast(null)}
        >
          {actionToast?.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
